<script setup lang="ts">
const props = withDefaults(
  defineProps<{
    /** @default "button" */
    type?: "button" | "submit";
    /** @default "secondary" */
    variant?: "primary" | "secondary";
    /** @default "md" */
    size?: "sm" | "md";
    loading?: boolean;
    disabled?: boolean;
  }>(),
  {
    type: "button",
    variant: "secondary",
    size: "md",
    disabled: false,
  },
);

function handlePointerDown(e: PointerEvent) {
  if (props.loading || props.disabled) return;
  const button = e.currentTarget as HTMLButtonElement | null;
  if (!button || button.disabled) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

  const r = button.getBoundingClientRect();
  const blot = document.createElement("span");
  blot.className = "blot";
  const size = Math.max(r.width, r.height) * 2.4;
  blot.style.width = `${size}px`;
  blot.style.height = `${size}px`;
  blot.style.left = `${e.clientX - r.left}px`;
  blot.style.top = `${e.clientY - r.top}px`;
  button.appendChild(blot);
  blot.addEventListener("animationend", () => blot.remove());
}
</script>

<template>
  <button
    :type="props.type"
    :disabled="props.disabled || props.loading"
    @pointerdown="handlePointerDown"
    :class="[
      'site-button btn-ink inline-flex w-fit cursor-pointer items-center justify-center gap-2 rounded-[6px] border font-medium transition-all duration-150 disabled:cursor-not-allowed disabled:opacity-40 disabled:pointer-events-none',
      size === 'md' && 'px-4 py-3 text-sm',
      size === 'sm' && 'px-3.5 py-2 text-xs',
      variant === 'primary' &&
        'btn-ink--primary border-button-primary bg-button-primary text-button-on-primary',
      variant === 'secondary' &&
        'btn-ink--secondary border-button-secondary bg-button-secondary text-button-on-secondary',
    ]"
  >
    <Icon v-if="loading" name="lucide:loader-circle" class="spin relative z-1" />
    <span class="relative z-1 inline-flex items-center gap-2">
      <slot />
    </span>
  </button>
</template>

<style scoped>
@keyframes spin-animation {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin-animation 1s infinite linear;
}
</style>
